import React, { useMemo } from 'react';
import { Eye, FileText, Link2Off, Plus } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';

const normalizeInvoiceType = (type = '') => String(type || '').trim().toUpperCase();

const getInvoiceTypeLabel = (type = '') => {
  const normalized = normalizeInvoiceType(type);

  if (['SALES', 'OUTGOING', 'SALE', 'S', 'SALEINVOICE'].includes(normalized)) return 'Satış Faturası';
  if (['PURCHASE', 'INCOMING', 'BUY', 'P', 'PURCHASEINVOICE'].includes(normalized)) return 'Alış Faturası';
  if (['SALESRETURN', 'OUTGOINGRETURN'].includes(normalized)) return 'Satış İade Faturası';
  if (['PURCHASERETURN', 'INCOMINGRETURN'].includes(normalized)) return 'Alış İade Faturası';

  return type || 'Fatura';
};

const getInvoiceTypeTone = (type = '') => {
  const label = getInvoiceTypeLabel(type);

  if (label.includes('Satış')) {
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  }

  if (label.includes('Alış')) {
    return 'bg-blue-50 text-blue-700 border-blue-200';
  }

  return 'bg-slate-50 text-slate-600 border-slate-100';
};

const statusTone = {
  Paid: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  PartiallyPaid: 'bg-amber-50 text-amber-700 border-amber-100',
  Unpaid: 'bg-rose-50 text-rose-700 border-rose-100',
  Cancelled: 'bg-slate-100 text-slate-500 border-slate-200'
};

const OperationInvoicesTab = ({
  invoices = [],
  loading = false,
  onAddInvoice,
  onDetachInvoice,
  onOpenInvoice
}) => {
  const totals = useMemo(() => invoices.reduce((acc, invoice) => {
    const normalizedType = normalizeInvoiceType(invoice.type);
    acc.count += 1;
    if (['SALES', 'OUTGOING', 'SALE', 'S', 'SALEINVOICE'].includes(normalizedType)) acc.sales += 1;
    if (['PURCHASE', 'INCOMING', 'BUY', 'P', 'PURCHASEINVOICE'].includes(normalizedType)) acc.purchase += 1;
    if (['SALESRETURN', 'OUTGOINGRETURN', 'PURCHASERETURN', 'INCOMINGRETURN'].includes(normalizedType)) acc.returns += 1;
    return acc;
  }, {
    count: 0,
    sales: 0,
    purchase: 0,
    returns: 0
  }), [invoices]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="rounded-[2rem] border border-slate-100 bg-white p-5 shadow-sm">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">İlişkili Fatura</p>
          <p className="mt-2 text-2xl font-black text-slate-900">{totals.count}</p>
        </div>
        <div className="rounded-[2rem] border border-emerald-100 bg-emerald-50/70 p-5">
          <p className="text-[10px] font-black uppercase tracking-widest text-emerald-500">Gelir Faturası</p>
          <p className="mt-2 text-2xl font-black text-emerald-700">{totals.sales}</p>
        </div>
        <div className="rounded-[2rem] border border-rose-100 bg-rose-50/70 p-5">
          <p className="text-[10px] font-black uppercase tracking-widest text-rose-500">Gider Faturası</p>
          <p className="mt-2 text-2xl font-black text-rose-700">{totals.purchase}</p>
        </div>
        <div className="rounded-[2rem] border border-amber-100 bg-amber-50/70 p-5">
          <p className="text-[10px] font-black uppercase tracking-widest text-amber-500">İade Kaydı</p>
          <p className="mt-2 text-2xl font-black text-amber-700">{totals.returns}</p>
        </div>
      </div>

      <div className="flex flex-col gap-4 rounded-[2rem] border border-slate-100 bg-white p-5 shadow-sm md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg font-black text-slate-800">Operasyon Faturaları</h2>
          <p className="text-xs font-bold text-slate-400">
            Bu operasyona bağlı gelir ve gider faturaları
          </p>
        </div>
        <button
          type="button"
          onClick={onAddInvoice}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-xs font-black text-white transition-colors hover:bg-black"
        >
          <Plus size={16} />
          Fatura Ekle
        </button>
      </div>

      <div className="overflow-hidden rounded-[2rem] border border-slate-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50/70">
              <tr className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                <th className="p-4">Fatura</th>
                <th className="p-4">Cari</th>
                <th className="p-4">Tip</th>
                <th className="p-4">Tarih</th>
                <th className="p-4 text-right">Tutar</th>
                <th className="p-4 text-right">Kalan</th>
                <th className="p-4">Durum</th>
                <th className="p-4 text-right">Aksiyon</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                <tr>
                  <td colSpan="8" className="p-16 text-center text-slate-400 font-bold">
                    Faturalar yükleniyor...
                  </td>
                </tr>
              ) : invoices.length > 0 ? (
                invoices.map((invoice) => (
                <tr
                  key={invoice._id}
                  className="group cursor-pointer transition-colors hover:bg-slate-50/80"
                  onClick={() => onOpenInvoice?.(invoice)}
                >
                    <td className="p-4">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenInvoice?.(invoice);
                        }}
                        className="text-left"
                      >
                        <div className="inline-flex items-center gap-2 font-black text-slate-800">
                          <FileText size={16} className="text-slate-400" />
                          {invoice.invoiceNumber || '-'}
                        </div>
                        <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          {invoice.currency || 'TRY'}
                        </div>
                      </button>
                    </td>
                    <td className="p-4">
                      <div className="font-bold text-slate-700">{invoice.accountId?.name || invoice.accountName || '-'}</div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        {invoice.accountId?.code || 'Kodsuz'}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex rounded-lg border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${getInvoiceTypeTone(invoice.type)}`}>
                        {getInvoiceTypeLabel(invoice.type)}
                      </span>
                    </td>
                    <td className="p-4 font-bold text-slate-600">
                      {invoice.date ? new Date(invoice.date).toLocaleDateString('tr-TR') : '-'}
                    </td>
                    <td className="p-4 text-right font-black text-slate-800">
                      {formatCurrency(invoice.grandTotal || 0, invoice.currency || 'TRY')}
                    </td>
                    <td className="p-4 text-right font-black text-slate-500">
                      {formatCurrency(invoice.remainingAmount || 0, invoice.currency || 'TRY')}
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex rounded-lg border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${statusTone[invoice.paymentStatus] || 'bg-slate-50 text-slate-600 border-slate-100'}`}>
                        {invoice.paymentStatus || '-'}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            onOpenInvoice?.(invoice);
                          }}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                          title="Faturayı önizle"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => onDetachInvoice?.(invoice)}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-amber-50 hover:text-amber-600"
                          title="İlişkiyi Kaldır"
                        >
                          <Link2Off size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="p-16 text-center">
                    <div className="mx-auto flex max-w-md flex-col items-center gap-3 rounded-[2rem] border border-dashed border-slate-200 bg-slate-50/70 px-6 py-12">
                      <FileText size={34} className="text-slate-300" />
                      <p className="text-sm font-bold text-slate-400">Bu operasyona bağlı fatura bulunmuyor.</p>
                      <button
                        type="button"
                        onClick={onAddInvoice}
                        className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 text-xs font-black text-white transition-colors hover:bg-blue-700"
                      >
                        <Plus size={14} />
                        İlk faturayı bağla
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OperationInvoicesTab;
